// DTOs shared between the Bun server and the React frontend.

export type Provider = "microsoft" | "google" | "imap";

export type ImapPreset = "icloud" | "yahoo" | "generic";

export interface ConnectImapRequest {
  preset: ImapPreset;
  email: string;
  password: string;
  host?: string;
  port?: number;
  smtpHost?: string;
  smtpPort?: number;
}

export interface AccountDto {
  id: number;
  provider: Provider;
  username: string;
  displayName: string | null;
  /** Stable pastel assigned to this account (hex). */
  color: string;
}

export interface ProfileDto {
  displayName: string;
  email: string | null;
  jobTitle?: string | null;
}

export interface AccountStatsDto {
  unreadCount: number;
  totalCount: number;
  nextEvent: {
    subject: string;
    start: string; // ISO, local timezone per Prefer header
    isAllDay: boolean;
  } | null;
}

export interface Recipient {
  name: string;
  address: string;
}

export interface MessageSummaryDto {
  id: string;
  subject: string;
  from: Recipient | null;
  receivedDateTime: string;
  bodyPreview: string;
  isRead: boolean;
  hasAttachments: boolean;
  importance: "low" | "normal" | "high";
  /** RFC 822 Message-ID header; captured at sync time for the local store. */
  internetMessageId?: string | null;
  /** Set by the thread endpoint: which folder this sibling lives in. */
  folderKind?: string;
  /** Set in the combined "All" view: which account this message came from. */
  origin?: { accountId: number; username: string; provider: Provider; color: string };
}

/** Virtual account id for the combined "All accounts" view. */
export const ALL_ACCOUNTS_ID = 0;

/** Local-store statistics for one account (dashboard). */
export interface AccountMailStatsDto {
  accountId: number;
  total: number;
  unread: number;
  byKind: Record<string, number>; // inbox/sent/deleted/junk/archive/custom
  summaryBytes: number;
  bodiesCached: number;
  bodyBytes: number;
  labeled: number; // messages with at least one AI-filter match
  oldest: string | null; // start of the stored window
  lastSyncedAt: string | null;
}

export interface MailStatsDto {
  accounts: AccountMailStatsDto[];
  totals: {
    total: number;
    unread: number;
    sent: number;
    summaryBytes: number;
    bodyBytes: number;
    bodiesCached: number;
    labeled: number;
  };
}

// --- AI Group studio (generate + test a filter prompt) ---

export interface GeneratePromptRequest {
  name: string;
  description: string;
}
export interface GeneratePromptResponse {
  prompt: string;
}

export interface TestFilterRequest {
  name: string;
  prompt: string;
}
export interface TestFilterResponse {
  total: number;
  matches: { id: string; subject: string; from: Recipient | null; receivedDateTime: string }[];
}

/** A manual pin: this email always belongs to this AI group (LLM skipped). */
export interface AiGroupEmailDto {
  id: number;
  filterId: number;
  accountId: number;
  messageId: string;
  fromName: string | null;
  fromAddress: string | null;
  subject: string;
  createdAt: string;
}

export interface PinEmailRequest {
  filterId: number;
  accountId: number;
  messageId: string; // raw provider id (combined prefix stripped by the client)
}

/** Progress of the "Save and run" history classification for an AI group. */
export interface ClassifyStatusDto {
  running: boolean;
  filterName: string;
  processed: number;
  total: number;
  matched: number;
  /** Emails that already carried a verdict for this group (skipped). */
  alreadyCategorized: number;
  /** OpenRouter spend for this run so far, in USD. */
  costUsd: number;
  error?: string;
}

/** OpenRouter spend totals (Settings → AI). */
export interface AiUsageDto {
  totalUsd: number;
  last30DaysUsd: number;
  calls: number;
}

/** One line of the server's in-memory sync event log (Details dialog). */
export interface SyncLogEntryDto {
  t: number; // epoch ms
  msg: string;
}

/** Per-account progress of the background mail sync. */
export interface SyncStatusDto {
  running: boolean;
  /** Wall-clock of the current/last sweep (epoch ms), for elapsed display. */
  startedAt?: number;
  finishedAt?: number;
  accounts: Record<
    number,
    {
      running: boolean;
      inserted: number;
      updated: number;
      /** Rows removed by deep-sync reconciliation (deleted in other clients). */
      removed?: number;
      /** True while the job is a deep-sync reconciliation. */
      reconcile?: boolean;
      backfillDone: boolean;
      error?: string;
      /** Set when the serialized chain reaches this account (absent = queued). */
      startedAt?: number;
      finishedAt?: number;
    }
  >;
}

export interface MessageListDto {
  messages: MessageSummaryDto[];
  nextLink: string | null;
  /** Combined view: accounts that couldn't be loaded (e.g. blocked tokens). */
  skipped?: string[];
}

export type FolderKind = "inbox" | "drafts" | "sent" | "deleted" | "junk" | "archive" | "custom";

export interface FolderDto {
  id: string;
  name: string;
  kind: FolderKind;
  unreadCount: number;
  totalCount: number;
}

export interface AttachmentDto {
  id: string;
  name: string;
  contentType: string;
  size: number;
  /** Signed URL on the isolated attachment origin (set by the API). */
  url?: string;
}

export interface MessageDetailDto {
  id: string;
  subject: string;
  from: Recipient | null;
  toRecipients: Recipient[];
  ccRecipients: Recipient[];
  receivedDateTime: string;
  bodyHtml: string;
  attachments: AttachmentDto[];
}

export interface SendMailRequest {
  to: string[];
  cc?: string[];
  subject: string;
  /** What the user typed, as HTML — WITHOUT the quoted original. */
  body: string;
  /**
   * Quoted original for replies/forwards. Graph ignores this and keeps the
   * reply draft's native Outlook-formatted history; Gmail/IMAP append it.
   */
  quotedHtml?: string;
  /** Set for replies/forwards so providers thread the conversation properly. */
  replyTo?: {
    /** Raw provider id of the original message. */
    messageId: string;
    mode: "reply" | "replyAll" | "forward";
    /** RFC 822 Message-ID of the original; enriched server-side from the store. */
    internetMessageId?: string | null;
  };
}

export interface EventDto {
  id: string;
  subject: string;
  start: string;
  end: string;
  isAllDay: boolean;
  location: string | null;
  organizer: string | null;
  isOnlineMeeting: boolean;
  /** Which account the event belongs to (set by the API for calendar coloring). */
  accountId?: number;
}

export interface CreateEventRequest {
  subject: string;
  start: string; // ISO
  end: string; // ISO
  isAllDay: boolean;
  location?: string;
}

/** Body of a 401 response when the account needs interactive re-auth. */
export interface ReauthError {
  reauth: true;
  message: string;
}

// --- AI mail filters ---

export type FilterColor = "amber" | "blue" | "green" | "red" | "purple" | "gray";

export interface FilterDto {
  id: number;
  name: string;
  prompt: string;
  color: FilterColor;
  enabled: boolean;
  order: number; // sidebar position (1..n)
  /** Per-group address, reserved for future actions (e.g. forwarding). */
  email?: string | null;
}

export interface SaveFilterRequest {
  name: string;
  prompt: string;
  color: FilterColor;
  enabled: boolean;
  order?: number; // omit on create to append at the end
  email?: string | null;
}

/** messageId → ids of filters that matched, for one account. */
export type LabelMapDto = Record<string, number[]>;

/** filterId → number of matched messages (per account, or summed for "All"). */
export type FilterCountsDto = Record<number, number>;

// --- Sender-based filters (exact From-address match), grouped ---

export interface SenderFilterDto {
  id: number;
  groupId: number;
  name: string;
  addresses: string[];
  color: FilterColor;
  rangeMonths: number | null; // null = all time
  order: number;
}

export interface SenderGroupDto {
  id: number;
  name: string;
  order: number;
  filters: SenderFilterDto[];
}

// --- Virtual folders (DB-only custom folders; provider mailbox untouched) ---

export interface VirtualFolderDto {
  id: number;
  name: string;
  color: FilterColor;
  order: number;
  unreadCount: number;
  totalCount: number;
  /** Auto-assign rule: sender addresses (empty = none) XOR an AI prompt. */
  ruleAddresses: string[];
  rulePrompt: string | null;
}

export type VirtualFolderRuleType = "none" | "addresses" | "ai";

export interface SaveVirtualFolderRequest {
  name: string;
  color?: FilterColor;
  order?: number;
  /** Omitted = leave the existing rule untouched (rename/recolor only). */
  ruleType?: VirtualFolderRuleType;
  ruleAddresses?: string[];
  rulePrompt?: string;
}

/** Assign (move) mail into a virtual folder. messageIds are RAW provider ids. */
export interface AssignVirtualFolderRequest {
  messages: { accountId: number; messageId: string }[];
}

/** Per-account provider-push subscription state (Graph / Gmail watch). */
export interface PushStatusDto {
  accountId: number;
  username: string;
  kind: string; // "graph" | "gmail"
  expiresAt: string;
  lastEventAt: string | null;
  lastError: string | null;
}

/** Progress of the background history scan after saving a folder rule. */
export interface FolderRuleScanStatusDto {
  running: boolean;
  folderName: string;
  processed: number;
  matched: number;
}

export interface SaveSenderGroupRequest {
  name: string;
  order?: number;
}

export interface SaveSenderFilterRequest {
  groupId: number;
  name: string;
  addresses: string[];
  color?: FilterColor;
  rangeMonths?: number | null;
  order?: number;
}

export interface CategorizeRequest {
  /** Enabled filters selected by the user. Omitted by older clients means all enabled filters. */
  filterIds?: number[];
  messages: {
    id: string;
    subject: string;
    from: string;
    preview: string;
  }[];
}

// --- app settings ---

export interface AppSettingsDto {
  aiModel: string;
  calendarStartHour: number;
  calendarEndHour: number;
  calendarDefaultView: string;
}

export interface ModelInfoDto {
  id: string;
  name: string;
  /** USD per 1M tokens, or null if pricing couldn't be fetched. */
  promptPricePerM: number | null;
  completionPricePerM: number | null;
}

// --- dev tools (owner-only) ---

export interface DevTableDto {
  name: string;
  /** pg_class.reltuples estimate — approximate, refreshed by autovacuum. */
  rowCount: number;
}

export interface DevTableRowsDto {
  columns: string[];
  /** Cell values pre-serialized to JSON primitives (long strings truncated). */
  rows: Record<string, unknown>[];
  /** Exact count of the selected table. */
  total: number;
}

/** One row of the "Search emails" grid (local store, deleted/junk excluded). */
export interface MailGridRowDto {
  /** Combined "accountId:rawId" — resolvable via the account-0 endpoints. */
  id: string;
  from: string;
  account: string;
  receivedDateTime: string;
  subject: string;
  preview: string;
}
